import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiX, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { FaDiscord } from 'react-icons/fa';
import { createShopItemOffer } from '../../../../store/shopItemOffers/shopItemOffersSlice';

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1200; padding: 20px;
`;

const Box = styled.div`
  background: #161616; border: 1px solid rgba(147,51,234,0.25);
  border-radius: 14px; padding: 24px; max-width: 400px; width: 100%; position: relative;

  h3 { font-size: 16px; font-weight: 800; color: #f1f5f9; margin: 0 0 4px; }
  p { font-size: 12.5px; color: #9ca3af; margin: 0 0 16px; line-height: 1.6; }

  .close-btn {
    position: absolute; top: 16px; inset-inline-end: 16px;
    background: none; border: none; color: #6b7280; cursor: pointer;
    font-size: 16px; display: flex;
    &:hover { color: #e2e8f0; }
  }
`;

const WarningBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.3);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 18px;
  svg { color: #f87171; font-size: 18px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 12.5px; color: #fca5a5; font-weight: 600; line-height: 1.5; }
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
  label { display: block; font-size: 12px; font-weight: 600; color: #9ca3af; margin-bottom: 7px; }
  input {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 10px 13px; color: #e2e8f0; font-size: 13.5px;
    font-family: inherit; outline: none; direction: ltr;
    &:focus { border-color: rgba(147,51,234,0.5); }
  }
  .hint { font-size: 11.5px; color: #6b7280; margin-top: 6px; }
`;

const Actions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;
  button {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 18px; border-radius: 9px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit; border: none; transition: opacity 0.15s;
  }
  .cancel-btn { background: transparent; color: #9ca3af; border: 1px solid rgba(255,255,255,0.1); }
  .cancel-btn:hover { border-color: rgba(255,255,255,0.2); color: #e2e8f0; }
  .primary-btn { background: #7C3AED; color: #fff; }
  .primary-btn:hover { opacity: 0.85; }
  .primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .discord-btn { background: #5865f2; color: #fff; width: 100%; justify-content: center; text-decoration: none; }
  .discord-btn:hover { opacity: 0.85; }
`;

const SuccessBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 12px 0 20px; text-align: center;
  svg { font-size: 46px; color: #34d399; }
  .title { font-size: 16px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 13px; color: #6b7280; max-width: 300px; }
`;

const WantedOfferModal = ({ item, discordInvite, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    setSubmitting(true);
    dispatch(createShopItemOffer({ item: item._id, offer_price: price ? Number(price) : undefined }))
      .unwrap()
      .then(() => setSubmitted(true))
      .finally(() => setSubmitting(false));
  };

  return (
    <Overlay onClick={onClose}>
      <Box onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label="close"><FiX /></button>

        {!submitted ? (
          <>
            <h3>{t('website.wanted_offer.title')}</h3>
            <p>{item.name}</p>

            <WarningBox>
              <FiAlertTriangle />
              <div className="msg">{t('website.wanted_offer.warning')}</div>
            </WarningBox>

            <FieldGroup>
              <label>{t('website.wanted_offer.different_price_label')}</label>
              <input
                type="number"
                min="0"
                placeholder={item.price_num}
                value={price}
                onChange={e => setPrice(e.target.value)}
              />
              <div className="hint">{t('website.wanted_offer.different_price_hint')}</div>
            </FieldGroup>

            <Actions>
              <button className="cancel-btn" onClick={onClose}>{t('website.buy_modal.cancel_btn')}</button>
              <button className="primary-btn" disabled={submitting} onClick={handleSubmit}>
                {t('website.wanted_offer.submit_btn')}
              </button>
            </Actions>
          </>
        ) : (
          <>
            <SuccessBox>
              <FiCheckCircle />
              <div className="title">{t('website.wanted_offer.success_title')}</div>
              <div className="sub">{t('website.wanted_offer.success_sub')}</div>
            </SuccessBox>
            <Actions style={{ flexDirection: 'column' }}>
              <a className="discord-btn" style={{ display: 'flex' }} href={discordInvite} target="_blank" rel="noreferrer">
                <FaDiscord /> {t('website.wanted_offer.open_ticket_btn')}
              </a>
              <button className="cancel-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={onClose}>
                {t('website.wanted_offer.close_btn')}
              </button>
            </Actions>
          </>
        )}
      </Box>
    </Overlay>
  );
};

export default WantedOfferModal;
